import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiExternalLink, FiTrendingUp, FiUsers, FiDollarSign, FiTarget, FiAward,
  FiBarChart2, FiActivity, FiPieChart, FiHome, FiCreditCard,
  FiGlobe, FiMapPin, FiInfo, FiFileText, FiLayers, FiTag,
  FiGitMerge, FiCornerDownRight, FiArrowUp, FiChevronDown
} from 'react-icons/fi';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../api';
import { formatCurrency, formatDate, formatPercentage, formatNumber, formatYear } from '../utils/formatters';
import { parseEmployeeHistory } from '../utils/parsers';
import { useResearchAgent } from '../context/ResearchAgentContext';
import { useDevice } from '../context/DeviceContext';
import './CompanyDetail.css';

// Custom hook for mobile collapsible sections
function useCollapsibleSections(sectionIds, defaultExpanded = true) {
  const [collapsed, setCollapsed] = useState(() => {
    const initial = {};
    sectionIds.forEach(id => { initial[id] = !defaultExpanded; });
    return initial;
  });

  const toggle = useCallback((sectionId) => {
    setCollapsed(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const isCollapsed = useCallback((sectionId) => collapsed[sectionId], [collapsed]);

  return { toggle, isCollapsed };
}

function CompanyDetail() {
  const { pitchbookId } = useParams();
  const navigate = useNavigate();
  const { updatePageContext, clearPageContext } = useResearchAgent();
  const { isMobile } = useDevice();
  const [company, setCompany] = useState(null);
  const [fullData, setFullData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkedCompetitors, setLinkedCompetitors] = useState([]);
  const [linkedInvestors, setLinkedInvestors] = useState([]);
  const [linkedDeals, setLinkedDeals] = useState([]);
  const [dealsStats, setDealsStats] = useState(null);
  const [acquirerData, setAcquirerData] = useState(null);
  const [acquisitionsData, setAcquisitionsData] = useState([]);
  const [parentData, setParentData] = useState(null);
  const [subsidiariesData, setSubsidiariesData] = useState([]);

  // Mobile collapsible sections
  const sectionIds = [
    'overview', 'quickFacts', 'transcend', 'metrics', 'vcPredictor',
    'financials', 'multiples', 'growth', 'deals', 'investors',
    'keywords', 'competitors', 'structure', 'maHistory', 'employees', 'web'
  ];
  const { toggle, isCollapsed } = useCollapsibleSections(sectionIds, true);

  // Mobile collapsible sections - DOM-based approach for existing markup
  useEffect(() => {
    if (!isMobile) return;

    // Find all section titles and make them collapsible
    const sectionTitles = document.querySelectorAll('.company-detail-page .section-title, .company-detail-page .subsection-title');

    const handleClick = (e) => {
      const title = e.currentTarget;
      const isCollapsed = title.classList.toggle('collapsed');

      // Find the next sibling elements until next section-title
      let sibling = title.nextElementSibling;
      while (sibling && !sibling.classList.contains('section-title') && !sibling.classList.contains('subsection-title')) {
        if (isCollapsed) {
          sibling.classList.add('section-collapsed');
        } else {
          sibling.classList.remove('section-collapsed');
        }
        sibling = sibling.nextElementSibling;
      }
    };

    sectionTitles.forEach(title => {
      title.addEventListener('click', handleClick);
      title.style.cursor = 'pointer';
    });

    return () => {
      sectionTitles.forEach(title => {
        title.removeEventListener('click', handleClick);
      });
    };
  }, [isMobile, fullData, company]); // Re-run when data loads

  // Collapsible section header component for mobile (for new sections)
  const SectionHeader = ({ id, icon, children, level = 'h2' }) => {
    const Tag = level;
    const collapsed = isMobile && isCollapsed(id);

    if (!isMobile) {
      return (
        <Tag className="section-title">
          {icon}
          {children}
        </Tag>
      );
    }

    return (
      <Tag
        className={`section-title collapsible-toggle ${collapsed ? 'collapsed' : ''}`}
        onClick={() => toggle(id)}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
      >
        <span className="section-title-content">
          {icon}
          {children}
        </span>
        <FiChevronDown className={`section-chevron ${collapsed ? '' : 'expanded'}`} />
      </Tag>
    );
  };

  // Collapsible content wrapper
  const SectionContent = ({ id, children }) => {
    if (!isMobile) return <>{children}</>;
    const collapsed = isCollapsed(id);
    return (
      <div className={`section-content-collapsible ${collapsed ? 'collapsed' : ''}`}>
        {children}
      </div>
    );
  };

  useEffect(() => {
    fetchCompanyData();
  }, [pitchbookId]);

  // Update Research Agent page context when company data loads
  useEffect(() => {
    if (company) {
      updatePageContext('company-detail', {
        company_name: company.name,  // Field is 'name' not 'company_name'
        pitchbook_id: company.pitchbook_id,
        primary_industry: company.primary_industry_code,
        verticals: Array.isArray(company.verticals) ? company.verticals : (typeof company.verticals === 'string' ? company.verticals.split(',').map(v => v.trim()) : []),
        description: company.description,
        hq_location: company.hq_location,
        year_founded: company.year_founded
      });
    }
    // Clear context when leaving the page
    return () => clearPageContext();
  }, [company, updatePageContext, clearPageContext]);

  const fetchCompanyData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both standard (with deals) and full data (with all columns)
      const [standardData, fullCompanyData] = await Promise.all([
        api.getCompanyDetail(pitchbookId),
        api.getCompanyFullDetail(pitchbookId)
      ]);
      setCompany(standardData);
      setFullData(fullCompanyData);

      // Fetch linked competitors and investors (non-blocking)
      fetchLinkedData(pitchbookId);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Company not found or failed to load.';
      setError(errorMessage);
      console.error('Error fetching company:', err);
      console.error('Error details:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedData = async (companyId) => {
    try {
      // Fetch competitors, investors, deals, acquirer, acquisitions, parent, subsidiaries in parallel
      const [
        competitorsData,
        investorsData,
        dealsData,
        acquirerResult,
        acquisitionsResult,
        parentResult,
        subsidiariesResult
      ] = await Promise.all([
        api.getCompanyCompetitors(companyId, { matched_only: true }).catch(() => ({ competitors: [] })),
        api.getCompanyInvestors(companyId).catch(() => ({ investors: [] })),
        api.getCompanyDeals(companyId, { limit: 50 }).catch(() => ({ deals: [], stats: null })),
        api.getCompanyAcquirer(companyId).catch(() => ({ acquirer: null, has_acquirer: false })),
        api.getCompanyAcquisitions(companyId).catch(() => ({ acquisitions: [] })),
        api.getCompanyParent(companyId).catch(() => ({ parent: null, has_parent: false })),
        api.getCompanySubsidiaries(companyId).catch(() => ({ subsidiaries: [] }))
      ]);
      setLinkedCompetitors(competitorsData.competitors || []);
      setLinkedInvestors(investorsData.investors || []);
      setLinkedDeals(dealsData.deals || []);
      setDealsStats(dealsData.stats || null);
      setAcquirerData(acquirerResult.has_acquirer ? acquirerResult : null);
      setAcquisitionsData(acquisitionsResult.acquisitions || []);
      setParentData(parentResult.has_parent ? parentResult : null);
      setSubsidiariesData(subsidiariesResult.subsidiaries || []);
    } catch (err) {
      console.error('Error fetching linked data:', err);
    }
  };

  const extractDomain = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url.replace('www.', '');
    }
  };

  // Shorten long ownership status tags for better display
  const shortenOwnershipStatus = (status) => {
    if (!status) return null;
    const shortenMap = {
      'Acquired/Merged (Operating Subsidiary)': 'Acquired/Merged',
      'Acquired/Merged (Operating Independently)': 'Acquired/Merged',
      'Out of Business (Bankruptcy)': 'Bankrupt',
      'Out of Business (Liquidation)': 'Liquidated',
      'Out of Business (Other)': 'Defunct',
      'Privately Held (Backing)': 'Private (Backed)',
      'Privately Held (No Backing)': 'Private',
    };
    return shortenMap[status] || status;
  };

  // Parse employee history for sparkline
  const getEmployeeData = () => {
    if (!fullData?.employee_history) return [];
    return parseEmployeeHistory(fullData.employee_history);
  };

  // Get financing timeline data from deals
  const getFinancingTimeline = () => {
    if (!company?.deals || company.deals.length === 0) return [];

    return company.deals
      .filter(deal => deal.deal_date)
      .sort((a, b) => new Date(a.deal_date) - new Date(b.deal_date))
      .map(deal => ({
        date: new Date(deal.deal_date).getFullYear(),
        fullDate: formatDate(deal.deal_date),
        amount: deal.deal_size_usd || 0,
        type: deal.deal_type || 'Unknown',
        valuation: deal.post_valuation || null
      }));
  };

  // Calculate months since last deal
  const getMonthsSinceLastDeal = () => {
    if (!fullData?.last_financing_date) return null;
    const lastDeal = new Date(fullData.last_financing_date);
    const now = new Date();
    const months = Math.floor((now - lastDeal) / (1000 * 60 * 60 * 24 * 30));
    return months;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading company intelligence...</p>
      </div>
    );
  }

  // Early return MUST happen before any data processing
  if (error || !company || !fullData) {
    return (
      <div className="error-container">
        <h2>⚠️ {error || 'Company not found'}</h2>
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Search
        </button>
      </div>
    );
  }

  // Safe to process data now - fullData is guaranteed to exist
  const employeeData = getEmployeeData();
  const timelineData = getFinancingTimeline();
  const monthsSinceLastDeal = getMonthsSinceLastDeal();

  // Check if VC predictor data available
  const hasVCPredictors = fullData?.opportunity_score || fullData?.success_probability;

  return (
    <div className="company-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          {/* Title row - company name only */}
          <h1 className="company-title">{company.name}</h1>

          {/* Meta row - location, tags, last updated - always below title */}
          <div className="header-meta-stack">
            {fullData.hq_location && (
              <span className="header-meta-item">
                <FiMapPin size={14} />
                {fullData.hq_location}
              </span>
            )}
            <div className="header-tags">
              {fullData.primary_industry_code && (
                <span className="industry-tag">{fullData.primary_industry_code}</span>
              )}
              {fullData.verticals && (Array.isArray(fullData.verticals) || typeof fullData.verticals === 'string') && (
                <span className="vertical-tag">
                  {Array.isArray(fullData.verticals) ? fullData.verticals.filter(v => v).join(', ') : String(fullData.verticals)}
                </span>
              )}
            </div>
            {fullData.last_updated_date && (
              <span className="last-updated-date">
                Last Updated: {formatDate(fullData.last_updated_date)}
              </span>
            )}
          </div>
        </div>
        <div className="header-right">
          {/* Ownership badge at top right */}
          {fullData.ownership_status && typeof fullData.ownership_status === 'string' && (
            <span className={`ownership-badge ${
              fullData.ownership_status.toLowerCase().includes('public') ? 'public' :
              fullData.ownership_status.toLowerCase().includes('acquired') || fullData.ownership_status.toLowerCase().includes('merged') ? 'acquired' :
              fullData.ownership_status.toLowerCase().includes('out of business') || fullData.ownership_status.toLowerCase().includes('defunct') ? 'out-of-business' :
              'private'
            }`}>
              {shortenOwnershipStatus(fullData.ownership_status)}
            </span>
          )}
          {/* Links below badge */}
          <div className="header-links">
            {company.website && typeof company.website === 'string' && (
              <a
                href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="website-link"
              >
                <FiExternalLink size={18} />
                {extractDomain(company.website)}
              </a>
            )}
            {fullData.linkedin_url && typeof fullData.linkedin_url === 'string' && (
              <a
                href={fullData.linkedin_url.startsWith('http') ? fullData.linkedin_url : `https://${fullData.linkedin_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="linkedin-link"
              >
                <FiExternalLink size={18} />
                LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Company Overview Section - NEW: Description + Quick Facts at Top */}
      <div className="company-overview-section">
        <div className="overview-grid">
          {/* Left: Description */}
          {company.description && (
            <div className="overview-description">
              <SectionHeader id="overview" icon={<FiFileText size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />}>
                Company Overview
              </SectionHeader>
              <SectionContent id="overview">
                <p className="description-text">{company.description}</p>
              </SectionContent>
            </div>
          )}

          {/* Middle: Quick Facts - only show facts NOT already in header */}
          <div className="overview-facts">
            <SectionHeader id="quickFacts" level="h3" icon={<FiInfo size={20} style={{marginRight: '8px', verticalAlign: 'middle'}} />}>
              Quick Facts
            </SectionHeader>
            <SectionContent id="quickFacts">
            <div className="facts-grid">
              {fullData.year_founded && (
                <div className="fact-item">
                  <span className="fact-icon"><FiTarget size={16} /></span>
                  <div className="fact-content">
                    <span className="fact-label">Founded</span>
                    <span className="fact-value">{formatYear(fullData.year_founded)}</span>
                  </div>
                </div>
              )}
              {fullData.employees && (
                <div className="fact-item">
                  <span className="fact-icon"><FiUsers size={16} /></span>
                  <div className="fact-content">
                    <span className="fact-label">Employees</span>
                    <span className="fact-value">{fullData.employees.toLocaleString()}</span>
                  </div>
                </div>
              )}
              {fullData.business_status && (
                <div className="fact-item">
                  <span className="fact-icon"><FiActivity size={16} /></span>
                  <div className="fact-content">
                    <span className="fact-label">Status</span>
                    <span className="fact-value">{fullData.business_status}</span>
                  </div>
                </div>
              )}
              {fullData.company_financing_status && (
                <div className="fact-item">
                  <span className="fact-icon"><FiTrendingUp size={16} /></span>
                  <div className="fact-content">
                    <span className="fact-label">Financing Status</span>
                    <span className="fact-value">{fullData.company_financing_status}</span>
                  </div>
                </div>
              )}
            </div>
            </SectionContent>
          </div>

          {/* Right: Transcend Classification */}
          {(fullData.transcend_segment || fullData.transcend_vertical ||
            (fullData.transcend_core_offering && fullData.transcend_core_offering.length > 0) ||
            (fullData.transcend_audience && fullData.transcend_audience.length > 0) ||
            (fullData.transcend_technology_core && fullData.transcend_technology_core.length > 0) ||
            (fullData.transcend_business_model && fullData.transcend_business_model.length > 0)) && (
            <div className="overview-transcend">
              <SectionHeader id="transcend" level="h3" icon={<FiTag size={20} style={{marginRight: '8px', verticalAlign: 'middle'}} />}>
                Transcend Classification
              </SectionHeader>
              <SectionContent id="transcend">
              <div className="transcend-grid">
                {fullData.transcend_segment && (
                  <div className="transcend-item">
                    <span className="transcend-label">Segment</span>
                    <span className="transcend-value segment">{fullData.transcend_segment}</span>
                  </div>
                )}
                {fullData.transcend_vertical && Array.isArray(fullData.transcend_vertical) && fullData.transcend_vertical.length > 0 && (
                  <div className="transcend-item">
                    <span className="transcend-label">Vertical</span>
                    <div className="transcend-tags">
                      {fullData.transcend_vertical.filter(v => v && String(v).trim()).map((v, i) => (
                        <span key={i} className="transcend-tag vertical">{String(v).trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {fullData.transcend_core_offering && Array.isArray(fullData.transcend_core_offering) && fullData.transcend_core_offering.length > 0 && (
                  <div className="transcend-item">
                    <span className="transcend-label">Core Offering</span>
                    <div className="transcend-tags">
                      {fullData.transcend_core_offering.filter(v => v && String(v).trim()).map((v, i) => (
                        <span key={i} className="transcend-tag offering">{String(v).trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {fullData.transcend_audience && Array.isArray(fullData.transcend_audience) && fullData.transcend_audience.length > 0 && (
                  <div className="transcend-item">
                    <span className="transcend-label">Audience</span>
                    <div className="transcend-tags">
                      {fullData.transcend_audience.filter(v => v && String(v).trim()).map((v, i) => (
                        <span key={i} className="transcend-tag audience">{String(v).trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {fullData.transcend_technology_core && Array.isArray(fullData.transcend_technology_core) && fullData.transcend_technology_core.length > 0 && (
                  <div className="transcend-item">
                    <span className="transcend-label">Technology Core</span>
                    <div className="transcend-tags">
                      {fullData.transcend_technology_core.filter(v => v && String(v).trim()).map((v, i) => (
                        <span key={i} className="transcend-tag tech">{String(v).trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {fullData.transcend_business_model && Array.isArray(fullData.transcend_business_model) && fullData.transcend_business_model.length > 0 && (
                  <div className="transcend-item">
                    <span className="transcend-label">Business Model</span>
                    <div className="transcend-tags">
                      {fullData.transcend_business_model.filter(v => v && String(v).trim()).map((v, i) => (
                        <span key={i} className="transcend-tag model">{String(v).trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              </SectionContent>
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics & Financial Performance - Side by Side */}
      <div className="metrics-financials-row">
        {/* Left: Key Metrics */}
        <div className="highlights-section">
          <SectionHeader id="metrics" icon={null}>Key Metrics</SectionHeader>
          <SectionContent id="metrics">

          {/* Key Metrics Row */}
          <div className="highlights-row">
            {/* Total Raised */}
          <div className="highlight-card">
            <div className="highlight-icon">
              <FiDollarSign size={24} />
            </div>
            <div className="highlight-content">
              <span className="highlight-label">Total Raised to Date</span>
              <span className="highlight-value">{formatCurrency(fullData.total_raised || company.total_raised_usd)}</span>
              {fullData.last_financing_date && (
                <span className="highlight-subtext">As of {formatDate(fullData.last_financing_date)}</span>
              )}
            </div>
          </div>

          {/* Last Deal Details */}
          {fullData.last_financing_size && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiTrendingUp size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Last Financing Deal</span>
                <span className="highlight-value">{formatCurrency(fullData.last_financing_size)}</span>
                {fullData.last_financing_date && (
                  <span className="highlight-subtext">{formatDate(fullData.last_financing_date)}</span>
                )}
                <span className="highlight-subtext">
                  {fullData.last_financing_deal_type || 'N/A'}
                  {fullData.last_financing_deal_class && ` - ${fullData.last_financing_deal_class}`}
                </span>
                {monthsSinceLastDeal && (
                  <span className="highlight-subtext">{monthsSinceLastDeal} months ago</span>
                )}
              </div>
            </div>
          )}

          {/* Last Debt Financing */}
          {fullData.last_financing_debt_size && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiCreditCard size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Last Debt Financing</span>
                <span className="highlight-value">{formatCurrency(fullData.last_financing_debt_size)}</span>
                {fullData.last_financing_debt_date && (
                  <span className="highlight-subtext">{formatDate(fullData.last_financing_debt_date)}</span>
                )}
              </div>
            </div>
          )}

          {/* Post Valuation */}
          {fullData.last_known_valuation && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiTarget size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Post Valuation</span>
                <span className="highlight-value">{formatCurrency(fullData.last_known_valuation)}</span>
                {fullData.last_known_valuation_date && (
                  <span className="highlight-subtext">As of {formatDate(fullData.last_known_valuation_date)}</span>
                )}
              </div>
            </div>
          )}

          {/* Valuation Step-up */}
          {fullData.last_valuation_stepup && !isNaN(parseFloat(fullData.last_valuation_stepup)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiAward size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Valuation Step-up</span>
                <span className="highlight-value">{parseFloat(fullData.last_valuation_stepup).toFixed(2)}x</span>
                <span className="highlight-subtext">From previous round</span>
              </div>
            </div>
          )}
        </div>

        {/* VC Predictor Row (if available) */}
        {hasVCPredictors && (
          <div className="vc-predictor-section">
            <h3 className="subsection-title"><FiAward size={20} style={{marginRight: '8px', verticalAlign: 'middle'}} /> VC Exit Predictor</h3>
            <div className="predictor-row">
              {/* Opportunity Score */}
              {fullData.opportunity_score && (
                <div className="predictor-card">
                  <span className="predictor-label">Opportunity Score</span>
                  <div className="predictor-score">
                    <span className="score-value">{fullData.opportunity_score}</span>
                    <div className="score-bar">
                      <div className="score-fill" style={{ width: `${fullData.opportunity_score}%` }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Probability */}
              {fullData.success_probability && (
                <div className="predictor-card">
                  <span className="predictor-label">Success Probability</span>
                  <span className="predictor-value">{formatPercentage(fullData.success_probability)}</span>
                  {fullData.success_class && (
                    <span className="predictor-subtext">{fullData.success_class}</span>
                  )}
                </div>
              )}

              {/* Exit Type Prediction */}
              {fullData.predicted_exit_type && (
                <div className="predictor-card exit-type">
                  <span className="predictor-label">Predicted Exit Type</span>
                  <span className="predictor-value large">{fullData.predicted_exit_type}</span>
                  <div className="exit-breakdown">
                    {fullData.ipo_probability && (
                      <span className="exit-stat">IPO: {formatPercentage(fullData.ipo_probability)}</span>
                    )}
                    {fullData.ma_probability && (
                      <span className="exit-stat">M&A: {formatPercentage(fullData.ma_probability)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </SectionContent>
      </div>

        {/* Right: Financial Performance */}
        {(fullData.revenue || fullData.gross_profit || fullData.ebitda || fullData.market_cap) && (
          <div className="financial-dashboard">
          <h2 className="section-title"><FiDollarSign size={24} style={{marginRight: '10px', verticalAlign: 'middle'}} /> Financial Performance</h2>

          {/* Primary Financial Metrics */}
          <div className="financial-grid">
            {fullData.revenue && !isNaN(parseFloat(fullData.revenue)) && (
              <div className="financial-card revenue">
                <div className="fin-icon"><FiDollarSign size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">Revenue</span>
                  <span className="fin-value">{formatCurrency(fullData.revenue)}</span>
                  {fullData.revenue_growth && !isNaN(parseFloat(fullData.revenue_growth)) && (
                    <span className={`fin-growth ${parseFloat(fullData.revenue_growth) >= 0 ? 'positive' : 'negative'}`}>
                      {parseFloat(fullData.revenue_growth) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(fullData.revenue_growth)).toFixed(1)}%
                    </span>
                  )}
                  {fullData.fiscal_period && (
                    <span className="fin-period">{fullData.fiscal_period}</span>
                  )}
                </div>
              </div>
            )}

            {fullData.gross_profit && !isNaN(parseFloat(fullData.gross_profit)) && (
              <div className="financial-card">
                <div className="fin-icon"><FiBarChart2 size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">Gross Profit</span>
                  <span className="fin-value">{formatCurrency(fullData.gross_profit)}</span>
                  {fullData.revenue && !isNaN(parseFloat(fullData.revenue)) && parseFloat(fullData.revenue) > 0 && (
                    <span className="fin-margin">
                      Margin: {((parseFloat(fullData.gross_profit) / parseFloat(fullData.revenue)) * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            )}

            {fullData.ebitda && !isNaN(parseFloat(fullData.ebitda)) && (
              <div className="financial-card">
                <div className="fin-icon"><FiTrendingUp size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">EBITDA</span>
                  <span className="fin-value">{formatCurrency(fullData.ebitda)}</span>
                  {fullData.revenue && !isNaN(parseFloat(fullData.revenue)) && parseFloat(fullData.revenue) > 0 && (
                    <span className="fin-margin">
                      Margin: {((parseFloat(fullData.ebitda) / parseFloat(fullData.revenue)) * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            )}

            {fullData.ebit && !isNaN(parseFloat(fullData.ebit)) && (
              <div className="financial-card">
                <div className="fin-icon"><FiActivity size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">EBIT</span>
                  <span className="fin-value">{formatCurrency(fullData.ebit)}</span>
                </div>
              </div>
            )}

            {fullData.net_income && !isNaN(parseFloat(fullData.net_income)) && (
              <div className="financial-card">
                <div className="fin-icon"><FiPieChart size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">Net Income</span>
                  <span className="fin-value">{formatCurrency(fullData.net_income)}</span>
                  {fullData.revenue && !isNaN(parseFloat(fullData.revenue)) && parseFloat(fullData.revenue) > 0 && (
                    <span className="fin-margin">
                      Margin: {((parseFloat(fullData.net_income) / parseFloat(fullData.revenue)) * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            )}

            {fullData.market_cap && !isNaN(parseFloat(fullData.market_cap)) && (
              <div className="financial-card valuation">
                <div className="fin-icon"><FiHome size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">Market Cap</span>
                  <span className="fin-value">{formatCurrency(fullData.market_cap)}</span>
                </div>
              </div>
            )}

            {fullData.enterprise_value && !isNaN(parseFloat(fullData.enterprise_value)) && (
              <div className="financial-card valuation">
                <div className="fin-icon"><FiTarget size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">Enterprise Value</span>
                  <span className="fin-value">{formatCurrency(fullData.enterprise_value)}</span>
                </div>
              </div>
            )}

            {fullData.net_debt && !isNaN(parseFloat(fullData.net_debt)) && (
              <div className="financial-card">
                <div className="fin-icon"><FiCreditCard size={20} /></div>
                <div className="fin-content">
                  <span className="fin-label">Net Debt</span>
                  <span className={`fin-value ${parseFloat(fullData.net_debt) < 0 ? 'positive' : ''}`}>
                    {formatCurrency(fullData.net_debt)}
                  </span>
                  <span className="fin-subtext">{parseFloat(fullData.net_debt) < 0 ? 'Net Cash' : ''}</span>
                </div>
              </div>
            )}
          </div>

          {/* Valuation Multiples */}
          {((fullData.enterprise_value && fullData.revenue) || (fullData.enterprise_value && fullData.ebitda)) && (
            <div className="multiples-section">
              <h3 className="subsection-title"><FiLayers size={20} style={{marginRight: "8px", verticalAlign: "middle"}} /> Valuation Multiples</h3>
              <div className="multiples-row">
                {fullData.enterprise_value && fullData.revenue &&
                 !isNaN(parseFloat(fullData.enterprise_value)) && !isNaN(parseFloat(fullData.revenue)) &&
                 parseFloat(fullData.revenue) > 0 && (
                  <div className="multiple-card">
                    <span className="multiple-label">EV / Revenue</span>
                    <span className="multiple-value">
                      {(parseFloat(fullData.enterprise_value) / parseFloat(fullData.revenue)).toFixed(2)}x
                    </span>
                  </div>
                )}

                {fullData.enterprise_value && fullData.ebitda &&
                 !isNaN(parseFloat(fullData.enterprise_value)) && !isNaN(parseFloat(fullData.ebitda)) &&
                 parseFloat(fullData.ebitda) > 0 && (
                  <div className="multiple-card">
                    <span className="multiple-label">EV / EBITDA</span>
                    <span className="multiple-value">
                      {(parseFloat(fullData.enterprise_value) / parseFloat(fullData.ebitda)).toFixed(2)}x
                    </span>
                  </div>
                )}

                {fullData.market_cap && fullData.revenue &&
                 !isNaN(parseFloat(fullData.market_cap)) && !isNaN(parseFloat(fullData.revenue)) &&
                 parseFloat(fullData.revenue) > 0 && (
                  <div className="multiple-card">
                    <span className="multiple-label">Market Cap / Revenue</span>
                    <span className="multiple-value">
                      {(parseFloat(fullData.market_cap) / parseFloat(fullData.revenue)).toFixed(2)}x
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Growth Metrics Dashboard */}
      {(fullData.growth_rate || fullData.web_growth_rate || fullData.similarweb_growth_rate) && (
        <div className="growth-dashboard">
          <h2 className="section-title"><FiTrendingUp size={24} style={{marginRight: "10px", verticalAlign: "middle"}} /> Growth Metrics</h2>
          <div className="growth-grid">
            {fullData.growth_rate && !isNaN(parseFloat(fullData.growth_rate)) && (
              <div className="growth-card primary">
                <div className="growth-header">
                  <span className="growth-label">Growth Rate</span>
                  {fullData.growth_rate_percentile && (
                    <span className="percentile-badge">{parseFloat(fullData.growth_rate_percentile).toFixed(0)}th percentile</span>
                  )}
                </div>
                <span className="growth-value">{parseFloat(fullData.growth_rate).toFixed(1)}%</span>
                {fullData.growth_rate_change && !isNaN(parseFloat(fullData.growth_rate_change)) && (
                  <span className={`growth-change ${parseFloat(fullData.growth_rate_change) >= 0 ? 'positive' : 'negative'}`}>
                    {parseFloat(fullData.growth_rate_change) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(fullData.growth_rate_change)).toFixed(1)}% change
                  </span>
                )}
              </div>
            )}

            {fullData.web_growth_rate && !isNaN(parseFloat(fullData.web_growth_rate)) && (
              <div className="growth-card">
                <div className="growth-header">
                  <span className="growth-label">Web Growth Rate</span>
                  {fullData.web_growth_rate_percentile && (
                    <span className="percentile-badge">{parseFloat(fullData.web_growth_rate_percentile).toFixed(0)}th percentile</span>
                  )}
                </div>
                <span className="growth-value">{parseFloat(fullData.web_growth_rate).toFixed(1)}%</span>
              </div>
            )}

            {fullData.similarweb_growth_rate && !isNaN(parseFloat(fullData.similarweb_growth_rate)) && (
              <div className="growth-card">
                <div className="growth-header">
                  <span className="growth-label">SimilarWeb Growth</span>
                  {fullData.similarweb_growth_rate_percentile && (
                    <span className="percentile-badge">{parseFloat(fullData.similarweb_growth_rate_percentile).toFixed(0)}th percentile</span>
                  )}
                </div>
                <span className="growth-value">{parseFloat(fullData.similarweb_growth_rate).toFixed(1)}%</span>
              </div>
            )}

            {fullData.majestic_growth_rate && !isNaN(parseFloat(fullData.majestic_growth_rate)) && (
              <div className="growth-card">
                <div className="growth-header">
                  <span className="growth-label">Majestic Growth</span>
                  {fullData.majestic_growth_rate_percentile && (
                    <span className="percentile-badge">{parseFloat(fullData.majestic_growth_rate_percentile).toFixed(0)}th percentile</span>
                  )}
                </div>
                <span className="growth-value">{parseFloat(fullData.majestic_growth_rate).toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deal History & Investors Side by Side */}
      {(linkedDeals.length > 0 ||
        (fullData.active_investors && Array.isArray(fullData.active_investors) && fullData.active_investors.length > 0) ||
        (fullData.former_investors && Array.isArray(fullData.former_investors) && fullData.former_investors.length > 0) ||
        linkedInvestors.length > 0) && (
        <div className="deals-investors-row">
          {/* Deal History */}
          {linkedDeals.length > 0 && (
            <div className="deal-history-section">
              <div className="deals-card">
                <h2 className="section-title">
                  <FiDollarSign size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />
                  Deal History
                  <span className="deal-count">{dealsStats?.total_deals || linkedDeals.length} deals</span>
                </h2>

                <div className="deals-list">
                  {linkedDeals
                    .sort((a, b) => new Date(b.deal_date || 0) - new Date(a.deal_date || 0))
                    .map((deal) => (
                      <Link
                        key={deal.pitchbook_deal_id}
                        to={`/deal/${deal.pitchbook_deal_id}`}
                        className="deal-item deal-item-linked"
                        style={{ textDecoration: 'none' }}
                      >
                        <div className="deal-header">
                          <span className="deal-type">{deal.deal_type || 'Unknown'}</span>
                          <span className="deal-date">{formatDate(deal.deal_date)}</span>
                        </div>
                        {deal.deal_type_2 && (
                          <div className="deal-subtype">{deal.deal_type_2}</div>
                        )}
                        <div className="deal-metrics">
                          {deal.deal_size_usd && (
                            <div className="deal-amount">{formatCurrency(deal.deal_size_usd)}</div>
                          )}
                          {deal.post_valuation_usd && (
                            <div className="deal-valuation">Post-Val: {formatCurrency(deal.post_valuation_usd)}</div>
                          )}
                        </div>
                        {deal.leadsole_investors && (
                          <div className="deal-lead-investors">
                            <span className="lead-label">Lead:</span> {
                              typeof deal.leadsole_investors === 'string'
                                ? deal.leadsole_investors.replace(/[{}]/g, '').split(',').slice(0, 2).join(', ')
                                : Array.isArray(deal.leadsole_investors)
                                  ? deal.leadsole_investors.slice(0, 2).join(', ')
                                  : ''
                            }
                          </div>
                        )}
                        <FiExternalLink size={14} className="deal-link-icon" />
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Investors */}
          {((fullData.active_investors && Array.isArray(fullData.active_investors) && fullData.active_investors.length > 0) ||
            (fullData.former_investors && Array.isArray(fullData.former_investors) && fullData.former_investors.length > 0) ||
            linkedInvestors.length > 0) && (
            <div className="investors-section">
              <div className="investors-card">
                <h2 className="section-title">
                  <FiUsers size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />
                  Investors
                </h2>

                {/* Active Investors */}
                {fullData.active_investors && Array.isArray(fullData.active_investors) && fullData.active_investors.length > 0 && (
                  <>
                    <h3 className="subsection-label">Active Investors</h3>
                    <div className="investors-list">
                      {fullData.active_investors.filter(inv => inv && String(inv).trim()).map((investor, index) => {
                        const investorName = String(investor).trim();
                        const linked = linkedInvestors.find(li =>
                          li.investor_type === 'active' &&
                          (li.investor_name_in_company?.toLowerCase().includes(investorName.toLowerCase()) ||
                           investorName.toLowerCase().includes(li.investor_name?.toLowerCase()))
                        );

                        return linked ? (
                          <Link
                            key={index}
                            to={`/investor/${linked.pitchbook_investor_id}`}
                            className="investor-tag clickable"
                          >
                            {investorName}
                          </Link>
                        ) : (
                          <span key={index} className="investor-tag">
                            {investorName}
                          </span>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Former Investors */}
                {fullData.former_investors && Array.isArray(fullData.former_investors) && fullData.former_investors.length > 0 && (
                  <>
                    <h3 className="subsection-label">Former Investors</h3>
                    <div className="investors-list">
                      {fullData.former_investors.filter(inv => inv && String(inv).trim()).map((investor, index) => {
                        const investorName = String(investor).trim();
                        const linked = linkedInvestors.find(li =>
                          li.investor_type === 'former' &&
                          (li.investor_name_in_company?.toLowerCase().includes(investorName.toLowerCase()) ||
                           investorName.toLowerCase().includes(li.investor_name?.toLowerCase()))
                        );

                        return linked ? (
                          <Link
                            key={index}
                            to={`/investor/${linked.pitchbook_investor_id}`}
                            className="investor-tag clickable former"
                          >
                            {investorName}
                          </Link>
                        ) : (
                          <span key={index} className="investor-tag former">
                            {investorName}
                          </span>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Keywords & Competitors Side by Side */}
      {((fullData.keywords && Array.isArray(fullData.keywords) && fullData.keywords.length > 0) ||
        (fullData.competitors && (typeof fullData.competitors === 'string' || Array.isArray(fullData.competitors)))) && (
        <div className="keywords-competitors-row">
          {/* Keywords */}
          {fullData.keywords && Array.isArray(fullData.keywords) && fullData.keywords.length > 0 && (
            <div className="keywords-section">
              <div className="keywords-card">
                <h2 className="section-title">
                  <FiLayers size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />
                  Keywords
                </h2>
                <div className="keywords-list">
                  {fullData.keywords.filter(k => k && String(k).trim()).map((keyword, index) => (
                    <span key={index} className="keyword-tag">
                      {String(keyword).trim()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Competitors */}
          {fullData.competitors && (typeof fullData.competitors === 'string' || Array.isArray(fullData.competitors)) && (
            <div className="competitors-section">
              <div className="competitors-card">
                <h2 className="section-title">
                  <FiTarget size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />
                  Competitors
                </h2>
                <div className="competitors-list">
                  {(Array.isArray(fullData.competitors)
                    ? fullData.competitors
                    : String(fullData.competitors).split(',')
                  ).filter(c => c && String(c).trim()).map((competitor, index) => {
                    const competitorName = String(competitor).trim();
                    // Check if this competitor is linked
                    const linked = linkedCompetitors.find(lc =>
                      lc.competitor_name_in_company?.toLowerCase().includes(competitorName.toLowerCase()) ||
                      competitorName.toLowerCase().includes(lc.competitor_name?.toLowerCase())
                    );

                    return linked ? (
                      <Link
                        key={index}
                        to={`/company/${linked.competitor_company_id}`}
                        className="competitor-tag clickable"
                      >
                        {competitorName}
                      </Link>
                    ) : (
                      <span key={index} className="competitor-tag">
                        {competitorName}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Corporate Structure & M&A History - Side by Side */}
      {(parentData || subsidiariesData.length > 0 || acquirerData || acquisitionsData.length > 0) && (
        <div className="corporate-ma-row">
          {/* Corporate Structure */}
          {(parentData || subsidiariesData.length > 0) && (
            <div className="corporate-structure-section">
              <div className="corporate-card">
                <h2 className="section-title">
                  <FiCornerDownRight size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />
                  Corporate Structure
                </h2>

                {/* Parent Company */}
                {parentData && parentData.parent && (
                  <div className="structure-item parent-company">
                    <h3 className="structure-label">
                      <FiArrowUp size={16} style={{marginRight: '6px'}} />
                      Parent Company
                    </h3>
                    {parentData.is_linked ? (
                      <Link
                        to={`/company/${parentData.parent.parent_pitchbook_id}`}
                        className="structure-company-card linked"
                      >
                        <div className="structure-company-name">
                          {parentData.parent.parent_name || parentData.parent.parent_name_raw}
                          <FiExternalLink size={14} className="structure-link-icon" />
                        </div>
                        {parentData.parent.primary_industry_code && (
                          <span className="structure-meta">{parentData.parent.primary_industry_code}</span>
                        )}
                        {parentData.parent.hq_location && (
                          <span className="structure-meta"><FiMapPin size={12} /> {parentData.parent.hq_location}</span>
                        )}
                      </Link>
                    ) : (
                      <div className="structure-company-card unlinked">
                        <div className="structure-company-name">
                          {parentData.parent.parent_name_raw}
                          <span className="unlinked-badge">Not in Database</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Subsidiaries */}
                {subsidiariesData.length > 0 && (
                  <div className="structure-item subsidiaries">
                    <h3 className="structure-label">
                      <FiCornerDownRight size={16} style={{marginRight: '6px'}} />
                      Subsidiaries
                      <span className="structure-count">{subsidiariesData.length}</span>
                    </h3>
                    <div className="subsidiaries-list">
                      {subsidiariesData.map((sub, index) => (
                        <Link
                          key={index}
                          to={`/company/${sub.subsidiary_pitchbook_id}`}
                          className="subsidiary-tag"
                        >
                          {sub.subsidiary_name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* M&A History */}
          {(acquirerData || acquisitionsData.length > 0) && (
            <div className="ma-history-section">
              <div className="ma-card">
                <h2 className="section-title">
                  <FiGitMerge size={22} style={{marginRight: '10px', verticalAlign: 'middle'}} />
                  M&A History
                </h2>

                {/* Acquired By */}
                {acquirerData && acquirerData.acquirer && (
                  <div className="ma-item acquired-by">
                    <h3 className="ma-label">Acquired By</h3>
                    {acquirerData.is_linked ? (
                      <Link
                        to={`/company/${acquirerData.acquirer.acquirer_pitchbook_id}`}
                        className="ma-company-card linked"
                      >
                        <div className="ma-company-name">
                          {acquirerData.acquirer.acquirer_name || acquirerData.acquirer.acquirer_name_raw}
                          <FiExternalLink size={14} className="ma-link-icon" />
                        </div>
                        {acquirerData.acquirer.primary_industry_code && (
                          <span className="ma-meta">{acquirerData.acquirer.primary_industry_code}</span>
                        )}
                        {acquirerData.acquirer.hq_location && (
                          <span className="ma-meta"><FiMapPin size={12} /> {acquirerData.acquirer.hq_location}</span>
                        )}
                      </Link>
                    ) : (
                      <div className="ma-company-card unlinked">
                        <div className="ma-company-name">
                          {acquirerData.acquirer.acquirer_name_raw}
                          <span className="unlinked-badge">Not in Database</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Acquisitions Made */}
                {acquisitionsData.length > 0 && (
                  <div className="ma-item acquisitions-made">
                    <h3 className="ma-label">
                      Companies Acquired
                      <span className="ma-count">{acquisitionsData.length}</span>
                    </h3>
                    <div className="acquisitions-list">
                      {acquisitionsData.map((acq, index) => (
                        <Link
                          key={index}
                          to={`/company/${acq.acquired_pitchbook_id}`}
                          className="acquisition-tag"
                        >
                          {acq.acquired_company_name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Employee Growth Progression - Area Chart */}
      {employeeData.length > 2 && (
        <div className="employee-growth-section">
          <h2 className="section-title"><FiUsers size={24} style={{marginRight: '10px', verticalAlign: 'middle'}} /> Employee Growth Trajectory</h2>
          <div className="employee-growth-chart">
            <ResponsiveContainer width="100%" height={380}>
              <AreaChart data={employeeData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <defs>
                  <linearGradient id="employeeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2C3E50" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#2C3E50" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="#6b7280"
                  style={{ fontSize: '13px', fontWeight: 500 }}
                  tickMargin={10}
                />
                <YAxis
                  stroke="#6b7280"
                  tickFormatter={(value) => value.toLocaleString()}
                  style={{ fontSize: '13px', fontWeight: 500 }}
                  label={{
                    value: 'Employees',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: '14px', fill: '#6b7280', fontWeight: 600 }
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Employees') return [value.toLocaleString(), 'Employees'];
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Year ${label}`}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    padding: '12px'
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: '8px', color: '#111827' }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#2C3E50"
                  strokeWidth={3}
                  fill="url(#employeeGradient)"
                  name="Employees"
                  dot={{ fill: '#2C3E50', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: '#2C3E50' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Web Presence Metrics - Moved to bottom */}
      {(fullData.similarweb_unique_visitors || fullData.majestic_referring_domains) && (
        <div className="web-presence-dashboard">
          <h2 className="section-title"><FiGlobe size={24} style={{marginRight: "10px", verticalAlign: "middle"}} /> Web Presence & Engagement</h2>
          <div className="web-metrics-grid">
            {fullData.similarweb_unique_visitors && !isNaN(parseFloat(fullData.similarweb_unique_visitors)) && (
              <div className="web-metric-card">
                <div className="web-icon"><FiUsers size={20} /></div>
                <div className="web-content">
                  <span className="web-label">SimilarWeb Unique Visitors</span>
                  <span className="web-value">{formatNumber(parseFloat(fullData.similarweb_unique_visitors))}</span>
                  {fullData.similarweb_unique_visitors_change && !isNaN(parseFloat(fullData.similarweb_unique_visitors_change)) && (
                    <span className={`web-change ${parseFloat(fullData.similarweb_unique_visitors_change) >= 0 ? 'positive' : 'negative'}`}>
                      {parseFloat(fullData.similarweb_unique_visitors_change) >= 0 ? '↑' : '↓'} {formatNumber(Math.abs(parseFloat(fullData.similarweb_unique_visitors_change)))}
                      {fullData.similarweb_unique_visitors__change && ` (${parseFloat(fullData.similarweb_unique_visitors__change).toFixed(1)}%)`}
                    </span>
                  )}
                </div>
              </div>
            )}

            {fullData.majestic_referring_domains && !isNaN(parseFloat(fullData.majestic_referring_domains)) && (
              <div className="web-metric-card">
                <div className="web-icon">🔗</div>
                <div className="web-content">
                  <span className="web-label">Majestic Referring Domains</span>
                  <span className="web-value">{formatNumber(parseFloat(fullData.majestic_referring_domains))}</span>
                  {fullData.majestic_referring_domains_change && !isNaN(parseFloat(fullData.majestic_referring_domains_change)) && (
                    <span className={`web-change ${parseFloat(fullData.majestic_referring_domains_change) >= 0 ? 'positive' : 'negative'}`}>
                      {parseFloat(fullData.majestic_referring_domains_change) >= 0 ? '↑' : '↓'} {formatNumber(Math.abs(parseFloat(fullData.majestic_referring_domains_change)))}
                      {fullData.majestic_referring_domains__change && ` (${parseFloat(fullData.majestic_referring_domains__change).toFixed(1)}%)`}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyDetail;
